import { jsxs, jsx } from 'react/jsx-runtime';
import { c as classNames, h as useTheme, T as ThemeContext, d as EGDSContextProvider } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-cards.css';
import * as React from 'react';
import { i as EGDSLayoutFlex, j as EGDSLayoutFlexItem } from '../layout-flex-item/index-chunk.esm.js';

const EGDSCardContentSectionBorderMap = [
    ['top', 'block-start'],
    ['right', 'inline-end'],
    ['bottom', 'block-end'],
    ['left', 'inline-start'],
];
const EGDSCardContentSectionBorders = new Map(EGDSCardContentSectionBorderMap);
const EGDSCardContentSectionPaddingMap = [
    ['blockstart', 'block-start'],
    ['blockend', 'block-end'],
    ['inlinestart', 'inline-start'],
    ['inlineend', 'inline-end'],
];
const EGDSCardContentSectionPaddings = new Map(EGDSCardContentSectionPaddingMap);
const EGDSCardTagMap = [
    ['article', 'article'],
    ['aside', 'aside'],
    ['div', 'div'],
    ['footer', 'footer'],
    ['header', 'header'],
    ['section', 'section'],
    ['ul', 'ul'],
    ['ol', 'ol'],
    ['li', 'li'],
];
const EGDSCardTags = new Map(EGDSCardTagMap);
const EGDSCardPlaceholderStartPhaseMap = [
    ['dark', 'dark'],
    ['light', 'light'],
];
const EGDSCardPlaceholderStartPhases = new Map(EGDSCardPlaceholderStartPhaseMap);
const EGDSCardPlaceholderAspectRatioMap = [
    ['r1-1', 'ratio-1-1'],
    ['r1-2', 'ratio-1-2'],
    ['r1-3', 'ratio-1-3'],
    ['r1-4', 'ratio-1-4'],
    ['r1-5', 'ratio-1-5'],
    ['r1-6', 'ratio-1-6'],
];
const EGDSCardPlaceholderAspectRatios = new Map(EGDSCardPlaceholderAspectRatioMap);
const EGDSCardAspectRatioMap = [
    ['r1-1', 'ratio-1-1'],
    ['r1-2', 'ratio-1-2'],
    ['r1-3', 'ratio-1-3'],
    ['r1-4', 'ratio-1-4'],
    ['r1-5', 'ratio-1-5'],
    ['r1-6', 'ratio-1-6'],
];
const EGDSCardAspectRatios = new Map(EGDSCardAspectRatioMap);
const EGDSCardRoundCornersMap = [
    ['top-right', 'top-right'],
    ['top-left', 'top-left'],
    ['bottom-right', 'bottom-right'],
    ['bottom-left', 'bottom-left'],
    ['all', 'all'],
];
const EGDSCardRoundCorners = new Map(EGDSCardRoundCornersMap);
const EGDSCardFeaturedHeaderTitleTagMap = [
    ['h2', 'h2'],
    ['h3', 'h3'],
    ['h4', 'h4'],
    ['h5', 'h5'],
    ['h6', 'h6'],
    ['p', 'p'],
];
const EGDSCardFeaturedHeaderTitleTags = new Map(EGDSCardFeaturedHeaderTitleTagMap);
const EGDSCardBackgroundThemes = {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    HIGHLIGHT: 'highlight',
    GLOBAL_LOYALTY_STANDARD: 'global-loyalty-standard',
    GLOBAL_LOYALTY_LOW: 'global-loyalty-low',
    GLOBAL_LOYALTY_MIDDLE: 'global-loyalty-middle',
    GLOBAL_LOYALTY_HIGH: 'global-loyalty-high',
    GLOBAL_LOYALTY_EXTRAHIGH: 'global-loyalty-extra-high',
};

const getClassNameList$6 = classNames.bind(styles);
const EGDSCard = React.forwardRef((props, ref) => {
    const { backgroundTheme = EGDSCardBackgroundThemes.PRIMARY, border, children, className, overflow: initialOverflow, padded, placeholder, selected, tag, style, ...attributes } = props;
    let overflow = initialOverflow;
    let actionArea;
    let featuredHeader;
    let link;
    let placeholderChild;
    const newChildren = [];
    const Tag = ((tag && EGDSCardTags.get(tag)) || EGDSCardTags.get('div'));
    const childrenArray = React.Children.toArray(children);
    const { className: themeClasses, ...themeDataAttributes } = useTheme();
    const themeContext = React.useContext(ThemeContext);
    const isColorThemeContainer = themeContext?.color?.isColorThemeContainer;
    // Map through the children and place the ones we want in specific places
    React.Children.forEach(children, (child) => {
        if (!child) {
            return;
        }
        const displayName = child.type?.displayName;
        if (displayName === 'EGDSCarousel') {
            overflow = true;
        }
        switch (true) {
            case /(EGDS|Uitk)CardActionArea/.test(displayName):
                actionArea = child;
                break;
            case /(EGDS|Uitk)CardFeaturedHeader/.test(displayName):
                featuredHeader = child;
                break;
            case /(EGDS|Uitk)CardLink/.test(displayName):
                link = child;
                break;
            case /(EGDS|Uitk)CardPlaceholder/.test(displayName):
                placeholderChild = child;
                break;
            default:
                newChildren.push(child);
        }
    });
    const baseClass = 'uitk-card';
    const ratioClass = placeholder && EGDSCardAspectRatios.get(placeholder.aspectRatio);
    const isValidEGDSCardBackgroundTheme = (value) => Object.values(EGDSCardBackgroundThemes).includes(value);
    const backgroundClassFragment = backgroundTheme && isValidEGDSCardBackgroundTheme(backgroundTheme)
        ? backgroundTheme
        : EGDSCardBackgroundThemes.PRIMARY;
    const cssClassNames = getClassNameList$6(baseClass, `${baseClass}-roundcorner-all`, actionArea && `${baseClass}-has-action-area`, border && `${baseClass}-has-border`, placeholder && `${baseClass}-has-placeholder-border`, featuredHeader && `${baseClass}-has-featured-header`, link && `${baseClass}-has-link`, overflow && `${baseClass}-has-overflow`, padded && `${baseClass}-padded`, selected && `${baseClass}-selected`, !children && `${baseClass}-empty`, className, placeholder && `${baseClass}-${ratioClass}`, `${baseClass}-has-${backgroundClassFragment}-theme`, !isColorThemeContainer && themeClasses);
    if (placeholderChild) {
        return React.cloneElement(placeholderChild, {
            className: cssClassNames,
        });
    }
    if (childrenArray.length > 1) {
        return (jsxs(Tag, { className: cssClassNames, ...attributes, ...(!isColorThemeContainer && themeDataAttributes), ref: ref, style: style, children: [featuredHeader, newChildren, actionArea, link] }));
    }
    if (childrenArray.length === 1) {
        const firstChild = childrenArray[0];
        const cardCustomStyleProps = { ...firstChild?.props?.style, ...style };
        const decoratedChild = React.cloneElement(firstChild, {
            className: classNames(cssClassNames, firstChild?.props?.className),
            ...attributes,
            ...(!isColorThemeContainer && themeDataAttributes),
            ...(Object.keys(cardCustomStyleProps).length !== 0 && { style: cardCustomStyleProps }),
        });
        return decoratedChild;
    }
    return (jsx(Tag, { className: cssClassNames, ...attributes, ...(!isColorThemeContainer && themeDataAttributes), ref: ref, style: style }));
});
EGDSCard.displayName = 'EGDSCard';
// Temporary, unstable
const EGDSCardAloha = EGDSCard;

const getClassNameList$5 = classNames.bind(styles);
const EGDSCardActionArea = (props) => {
    const { className, children, ...attributes } = props;
    const baseClass = 'uitk-card-action-area';
    const cssClassNames = getClassNameList$5(baseClass, className);
    return (jsx("div", { className: cssClassNames, ...attributes, children: children }));
};
EGDSCardActionArea.displayName = 'EGDSCardActionArea';
// Temporary, unstable
const EGDSCardAlohaActionArea = EGDSCardActionArea;

const getClassNameList$4 = classNames.bind(styles);
const EGDSCardContentSection = (props) => {
    const { border, children, className, padded = true, tag, ...attributes } = props;
    let link;
    const newChildren = [];
    const Tag = ((tag && EGDSCardTags.get(tag)) || EGDSCardTags.get('div'));
    // Map through the children and place the ones we want in specific places
    React.Children.forEach(children, (child) => {
        if (!child) {
            return;
        }
        if (child.type.displayName?.match(/(EGDS|Uitk)CardLink/)) {
            link = child;
        }
        else {
            newChildren.push(child);
        }
    });
    const paddedClass = (prop, paddedBaseClass) => {
        const propType = typeof prop;
        const classStr = [];
        if (propType === 'boolean') {
            classStr.push(`${paddedBaseClass}-padded`);
        }
        else {
            prop &&
                prop.forEach((value) => classStr.push(`${paddedBaseClass}-padded-${EGDSCardContentSectionPaddings.get(value)}`));
        }
        return classStr.join(' ');
    };
    const baseClass = 'uitk-card-content-section';
    const cssClassNames = getClassNameList$4(baseClass, border && `${baseClass}-border-${EGDSCardContentSectionBorders.get(border)}`, link && `${baseClass}-has-link`, padded && paddedClass(padded, baseClass), className);
    return (jsxs(Tag, { className: cssClassNames, ...attributes, children: [newChildren, link] }));
};
EGDSCardContentSection.displayName = 'EGDSCardContentSection';
// Temporary, unstable
const EGDSCardAlohaContentSection = EGDSCardContentSection;

const getClassNameList$3 = classNames.bind(styles);
const EGDSCardFeaturedHeader = (props) => {
    const { children, className, title, ...attributes } = props;
    const baseClass = 'uitk-card-featured-header';
    const cssClassNames = getClassNameList$3(baseClass, 'uitk-card-roundcorner-top-right', 'uitk-card-roundcorner-top-left', className);
    const TitleTag = (title && title.tag) || EGDSCardFeaturedHeaderTitleTags.get('p');
    return (jsxs("header", { className: cssClassNames, ...attributes, children: [title && jsx(TitleTag, { className: getClassNameList$3('uitk-card-featured-header-title'), children: title.text }), children] }));
};
EGDSCardFeaturedHeader.displayName = 'EGDSCardFeaturedHeader';
// Temporary, unstable
const EGDSCardAlohaFeaturedHeader = EGDSCardFeaturedHeader;

const getClassNameList$2 = classNames.bind(styles);
const EGDSCardLink = (props) => {
    const { children } = props;
    const childrenArray = React.Children.toArray(children);
    const firstChild = childrenArray[0];
    const decoratedFirstChild = React.cloneElement(firstChild, {
        className: getClassNameList$2(firstChild.props.className, 'uitk-card-link'),
    }, jsx("span", { className: "is-visually-hidden", children: firstChild.props.children }));
    if (!childrenArray || childrenArray.length !== 1) {
        console.warn(`UitkCardLink uses the first child element. ${childrenArray.length} children found.`);
    }
    return decoratedFirstChild;
};
EGDSCardLink.displayName = 'EGDSCardLink';
// Temporary, unstable
const EGDSCardAlohaLink = EGDSCardLink;

const getClassNameList$1 = classNames.bind(styles);
const EGDSCardPlaceholder = (props) => {
    const { aspectRatio, children, className, startPhase = EGDSCardPlaceholderStartPhases.get('light'), ...attributes } = props;
    let secondaryButton;
    // Map through the children and place the ones we want in specific places
    React.Children.forEach(children, (child) => {
        if (!child) {
            return;
        }
        if (child.type.displayName?.match(/(EGDS|Uitk)SecondaryButton/)) {
            secondaryButton = child;
        }
    });
    const baseClass = 'uitk-card-placeholder';
    const ratioClass = aspectRatio
        ? EGDSCardPlaceholderAspectRatios.get(aspectRatio)
        : EGDSCardPlaceholderAspectRatios.get('r1-3');
    const cssClassNames = getClassNameList$1(baseClass, !secondaryButton && `${baseClass}-animate-from-${startPhase}`, secondaryButton && `${baseClass}-has-action`, className, `${baseClass}-${ratioClass}`);
    if (secondaryButton) {
        return (jsx("div", { className: cssClassNames, ...attributes, children: jsx(EGDSLayoutFlex, { alignItems: "center", className: getClassNameList$1('uitk-card-placeholder-has-action-inner'), justifyContent: "center", children: jsx(EGDSLayoutFlexItem, { children: secondaryButton }) }) }));
    }
    return jsx("div", { className: cssClassNames, ...attributes });
};
EGDSCardPlaceholder.displayName = 'EGDSCardPlaceholder';
// Temporary, unstable
const EGDSCardAlohaPlaceholder = EGDSCardPlaceholder;

const getClassNameList = classNames.bind(styles);
const EGDSCardBrandTheme = (props) => {
    const { children, className, brandTheme = 'dark' } = props;
    const baseClass = 'uitk-brand-theme-card';
    const cssClassNames = getClassNameList(className, baseClass, `${baseClass}-has-theme-${brandTheme}`);
    return (jsx(EGDSContextProvider, { config: {
            context: null,
            data: {
                contextClassNames: {
                    EGDSHeading: `${getClassNameList('uitk-brand-theme-card-heading')}`,
                },
            },
        }, children: jsx(EGDSCard, { ...props, className: cssClassNames, children: children }) }));
};
EGDSCardBrandTheme.displayName = 'EGDSCardBrandTheme';

export { EGDSCardContentSectionBorders as E, EGDSCardContentSectionPaddings as a, EGDSCardTags as b, EGDSCardPlaceholderStartPhases as c, EGDSCardPlaceholderAspectRatios as d, EGDSCardAspectRatios as e, EGDSCardRoundCorners as f, EGDSCardFeaturedHeaderTitleTags as g, EGDSCardBackgroundThemes as h, EGDSCard as i, EGDSCardAloha as j, EGDSCardActionArea as k, EGDSCardAlohaActionArea as l, EGDSCardContentSection as m, EGDSCardAlohaContentSection as n, EGDSCardFeaturedHeader as o, EGDSCardAlohaFeaturedHeader as p, EGDSCardLink as q, EGDSCardAlohaLink as r, EGDSCardPlaceholder as s, EGDSCardAlohaPlaceholder as t, EGDSCardBrandTheme as u };
//# sourceMappingURL=index-chunk.esm.js.map
